package com.anjuros.website.redesign.sections.contactforms

import com.anjuros.website.redesign.util.RdText

enum class DemoFieldType { TEXT, EMAIL, PHONE, AREA, SELECT, DATE, RADIO, CHECKS }

/**
 * One field in a demo form. [initial] is the example answer it starts with; for [DemoFieldType.CHECKS]
 * it lists the ticked options as "a|b". [options] lists the choices for selects, radios and checkboxes.
 */
class DemoField(
    val type: DemoFieldType,
    val label: RdText,
    val initial: RdText,
    val options: RdText? = null,
    val required: Boolean = false,
)

class DemoForm(
    val id: String,
    val tab: RdText,
    val title: RdText,
    val kind: RdText,
    val business: RdText,
    val fields: List<DemoField>,
)

private val NameField = DemoField(DemoFieldType.TEXT, RdText.FIELD_NAME, RdText.DEMO_NAME, required = true)
private val PhoneField = DemoField(DemoFieldType.PHONE, RdText.FIELD_PHONE, RdText.DEMO_PHONE, required = true)
private val EmailField = DemoField(DemoFieldType.EMAIL, RdText.FIELD_EMAIL, RdText.DEMO_EMAIL, required = true)
private val OptionalEmailField = DemoField(DemoFieldType.EMAIL, RdText.FIELD_EMAIL, RdText.DEMO_EMAIL)
private fun message(initial: RdText) = DemoField(DemoFieldType.AREA, RdText.FIELD_MESSAGE, initial, required = true)

/** The example forms, based on the ones on the previous Contact forms page. */
val DemoForms = listOf(
    DemoForm(
        "general", RdText.DEMO_GENERAL_TAB, RdText.DEMO_GENERAL_TITLE, RdText.DEMO_GENERAL_KIND, RdText.DEMO_GENERAL_BUSINESS,
        listOf(NameField, EmailField, PhoneField, message(RdText.DEMO_GENERAL_MESSAGE)),
    ),
    DemoForm(
        "electrician", RdText.DEMO_ELEC_TAB, RdText.DEMO_ELEC_TITLE, RdText.DEMO_ELEC_KIND, RdText.DEMO_ELEC_BUSINESS,
        listOf(
            NameField, EmailField, PhoneField,
            DemoField(DemoFieldType.SELECT, RdText.DEMO_ELEC_TYPE, RdText.DEMO_ELEC_TYPE_VALUE, RdText.DEMO_ELEC_TYPE_OPTIONS),
            message(RdText.DEMO_ELEC_MESSAGE),
        ),
    ),
    DemoForm(
        "hairdresser", RdText.DEMO_HAIR_TAB, RdText.DEMO_HAIR_TITLE, RdText.DEMO_HAIR_KIND, RdText.DEMO_HAIR_BUSINESS,
        listOf(
            NameField, PhoneField, message(RdText.DEMO_HAIR_MESSAGE),
            DemoField(DemoFieldType.DATE, RdText.DEMO_HAIR_DATE, RdText.DEMO_HAIR_DATE_VALUE),
        ),
    ),
    DemoForm(
        "plumber", RdText.DEMO_PLUMB_TAB, RdText.DEMO_PLUMB_TITLE, RdText.DEMO_PLUMB_KIND, RdText.DEMO_PLUMB_BUSINESS,
        listOf(
            NameField, PhoneField, message(RdText.DEMO_PLUMB_MESSAGE),
            DemoField(DemoFieldType.RADIO, RdText.DEMO_PLUMB_URGENCY, RdText.DEMO_PLUMB_URGENCY_VALUE, RdText.DEMO_PLUMB_URGENCY_OPTIONS, required = true),
        ),
    ),
    DemoForm(
        "decorator", RdText.DEMO_DECO_TAB, RdText.DEMO_DECO_TITLE, RdText.DEMO_DECO_KIND, RdText.DEMO_DECO_BUSINESS,
        listOf(
            NameField, PhoneField, OptionalEmailField,
            DemoField(DemoFieldType.CHECKS, RdText.DEMO_DECO_AREAS, RdText.DEMO_DECO_AREAS_VALUE, RdText.DEMO_DECO_AREAS_OPTIONS),
            DemoField(DemoFieldType.CHECKS, RdText.DEMO_DECO_WORK, RdText.DEMO_DECO_WORK_VALUE, RdText.DEMO_DECO_WORK_OPTIONS),
            message(RdText.DEMO_DECO_MESSAGE),
        ),
    ),
    DemoForm(
        "baker", RdText.DEMO_BAKE_TAB, RdText.DEMO_BAKE_TITLE, RdText.DEMO_BAKE_KIND, RdText.DEMO_BAKE_BUSINESS,
        listOf(
            NameField, PhoneField, OptionalEmailField,
            DemoField(DemoFieldType.SELECT, RdText.DEMO_BAKE_WHAT, RdText.DEMO_BAKE_WHAT_VALUE, RdText.DEMO_BAKE_WHAT_OPTIONS),
            DemoField(DemoFieldType.AREA, RdText.FIELD_NOTE, RdText.DEMO_BAKE_NOTE),
        ),
    ),
)

/** The electrician form opens first: it's the most common trade. */
const val DEFAULT_DEMO_FORM = 1
